import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router'
import { fetchSharedRoom } from '../lib/api'
import { uiStrings } from '../lib/strings'

/**
 * `/v/<key>` — a fitting room as a friend sees it: the lists and the gowns on
 * them, under the customer's first name. Nothing to press but the way to a
 * fitting room of one's own; the lists stay the customer's to change.
 */
export function SharedRoomRoute() {
  const t = uiStrings(navigator.language || 'en')
  const { shareToken = '' } = useParams<{ shareToken: string }>()
  const room = useQuery({
    queryKey: ['public', 'shared', shareToken] as const,
    queryFn: ({ signal }) => fetchSharedRoom(shareToken, signal),
    retry: false,
  })
  const data = room.data
  const lists = (data?.lists ?? []).filter((list) => list.gowns.length > 0)

  return (
    <div className="fr-room min-h-screen">
      <div className="mx-auto w-full max-w-[520px] px-5 py-10">
        {room.isPending ? (
          <p style={{ color: 'var(--fr-ink-dim)' }}>{t.loading}</p>
        ) : !data ? (
          <>
            <h1 className="fr-display text-[2rem] leading-[1.08]">{t.sharedTitle(undefined)}</h1>
            <p className="mt-3 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
              {t.sharedGone}
            </p>
          </>
        ) : (
          <>
            <p className="fr-eyebrow">{data.boutiqueName}</p>
            <h1 className="fr-display mt-2 text-[2rem] leading-[1.08]">{t.sharedTitle(data.firstName)}</h1>
            <p className="mt-3 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
              {t.sharedBody}
            </p>
            {lists.length === 0 ? (
              <p className="mt-8 text-[0.9375rem]" style={{ color: 'var(--fr-ink-faint)' }}>
                {t.sharedEmpty}
              </p>
            ) : (
              lists.map((list) => (
                <section key={list.key} className="mt-8">
                  <h2 className="fr-eyebrow mb-3" style={{ color: list.marker === 'primary' ? 'var(--fr-primary)' : 'var(--fr-secondary)' }}>
                    <span aria-hidden="true">{list.marker === 'primary' ? '★ ' : '♡ '}</span>
                    {list.label} · {list.gowns.length}
                  </h2>
                  <ul className="grid grid-cols-2 gap-3">
                    {list.gowns.map((gown) => (
                      <li key={gown.id}>
                        <div className="aspect-[3/4] overflow-hidden" style={{ background: 'var(--fr-raised)' }}>
                          {gown.imageUrl ? <img src={gown.imageUrl} alt="" className="h-full w-full object-cover" loading="lazy" /> : null}
                        </div>
                        <p className="fr-display mt-2 truncate text-[1rem] leading-tight">{gown.name}</p>
                        {gown.facts.length > 0 ? (
                          <p className="truncate text-[0.8125rem]" style={{ color: 'var(--fr-ink-dim)' }}>
                            {gown.facts.join(' · ')}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ))
            )}
            <section className="mt-10 border-t pt-6" style={{ borderColor: 'var(--fr-rule)' }}>
              <p className="text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
                {t.sharedStartOwnBody}
              </p>
              <a className="fr-btn fr-btn-primary mt-4 block w-full text-center" href="/start" data-testid="shared-start-own">
                {t.sharedStartOwn}
              </a>
            </section>
          </>
        )}
      </div>
    </div>
  )
}
